'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Navigation, Mousewheel, Keyboard, Virtual,
} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  ArrowUpIcon, ArrowDownIcon, SpeakerWaveIcon, SpeakerXMarkIcon, XMarkIcon,
} from '@heroicons/react/16/solid';
import {
  Button, LinkStyled, Modal,
} from '@components/common';
import React, {
  useCallback, useRef, useState, useEffect,
  useMemo,
} from 'react';
import {
  PauseIcon, PhoneIcon, PlayIcon,
} from '@heroicons/react/20/solid';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';
import { useAuthClient } from '@root/src/hooks';
import { useSwrResumeCheckUnlock } from '@hooks/useSwrApi/endpoints/resume-operations';
import { showAddCompanyInfoModal } from '@components/features/modals/employer/AddCompanyInfoModal';
import { showUpgradeSubscriptionModal } from '@components/features/modals/employer/subscription/UpgradeSubscriptionModal';
import { trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { StoriesActionIcons } from '@components/templates/stories/StoriesActionIcons';
import { useSwrStoriesFetchSingle } from '@hooks/useSwrApi/endpoints/stories/useSwrStoriesFetchSingle';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { useResumeAccessCheck } from '@components/templates/stories/hooks/useResumeAccesCheck';
import { showPremiumModal } from '@components/features/modals/premium/PremiumModal';
import { InterviewResolver } from '@components/features/interview-resolver';
import { useSwrResumeInterviewAnswered } from '@hooks/useSwrApi/endpoints/interview/useSwrResumeInterviewAnswered';
import {
  attachHls, findHlsStreamUrl, getHlsSupport, type HlsSupport,
} from '@utils/video/hlsPlayback';


interface SwiperStoriesCardsProps {
  locale: Locale;
  slug: string;
  onClose?: () => void;
  openTalentModal?: (userSlug: string, as?: string) => void,
}

export const SwiperStoriesCards = ({
  locale, slug, onClose, openTalentModal,
}: SwiperStoriesCardsProps) => {
  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;
  const videoRefs = useRef<HTMLVideoElement[]>([]);
  const [swiperIndex, setSwiperIndex] = useState(0);
  const { isAuth } = useAuthClient();

  // Per-card interview-reply gate: fetch answered questions once (SWR dedups); auth-only endpoint.
  const { answeredQuestionIds, answeredQuestionTexts } = useSwrResumeInterviewAnswered({ locale, shouldFetch: isAuth });

  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [muted, setMuted] = useState(true);
  const [hasInteracted, setHasinteracted] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const iconTimeout = useRef<NodeJS.Timeout | null>(null);
  const progressRefs = useRef<HTMLDivElement[]>([]);


  const { isChecking: isLoadingSecurityCheck, isAllowed: isAllowedPlayingVideo, check: checkResumeAccess } = useResumeAccessCheck(locale, slug);

  const {
    data,
  } = useSwrStoriesFetchSingle({
    locale,
    slug,
    shouldFetch: isAllowedPlayingVideo,
    errorCallback: (response) => {
      const code = response?.errors?.[0]?.code;
      if (code === 805) {
        // premium modal case
        showPremiumModal(locale, '', 805);
      }
    },
  });


  useEffect(() => {
    if (!slug) return;
    void checkResumeAccess(slug);
  }, [slug, checkResumeAccess]);

  const items: ApiSchema<'InterviewVideoRead2'>[] = useMemo(
    () => data?.flat() ?? [],
    [data],
  );

  // HLS capability is probed after mount only, so the first render agrees with SSR markup
  // (MP4 fallback); capable browsers then upgrade to adaptive streams.
  const [hlsSupport, setHlsSupport] = useState<HlsSupport>({ native: false, mse: false });
  useEffect(() => {
    setHlsSupport(getHlsSupport());
  }, []);

  const activeHlsUrl = findHlsStreamUrl(items[swiperIndex]?.streams);
  const activeMp4Url = items[swiperIndex]?.url;

  // MSE browsers play HLS through hls.js on the ACTIVE video only (its JSX src stays
  // undefined so React and hls.js don't fight); only MSE-less browsers (iPhone Safari)
  // get the manifest as plain src — canPlayType alone lies (desktop Chrome says "maybe").
  useEffect(() => {
    const video = videoRefs.current[swiperIndex];
    if (!video || !activeHlsUrl || !hlsSupport.mse) return undefined;
    return attachHls(video, activeHlsUrl, { autoplay: true, fallbackUrl: activeMp4Url });
  }, [swiperIndex, activeHlsUrl, activeMp4Url, hlsSupport]);

  // Track initial story view when stories are loaded
  useEffect(() => {
    if (items && items.length > 0) {
      sendGoogleTagEvent('viewed_interview', 'Custom', { source: 'talent_badge' });
    }
  }, [items]);

  const toggleMuteAll = useCallback((value?: string) => {
    if (!value) return;

    if (value === 'true') {
      setMuted(true);
    } else if (value === 'false') {
      setMuted(false);
    } else if (value === 'prev') {
      setMuted((prev) => !prev);
    }
  }, []);


  const { trigger: triggerUnlock } = useSwrResumeCheckUnlock({
    userSlug: slug ?? '',
    type: 'contact',
    locale,
    successCallback: () => {
      setConfirmModal(false);
      openTalentModal?.(slug);
    },
    errorCallback: (errors) => {
      const code = errors?.errors?.[0]?.code;

      if (!code) {
        console.error('Unlock failed:', errors);
        return;
      }

      if (code === 403) {
        showAddCompanyInfoModal(locale);
      } else if (code === 601) {
        showUpgradeSubscriptionModal();
      } else {
        console.error('Unlock failed:', errors);
      }
    },
  });

  const updateVideos = (activeIndex: number) => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === activeIndex) {
        if (isAllowedPlayingVideo && !isLoadingSecurityCheck) {
          void video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      } else {
        // Pause everything else — without this the previous video keeps playing (audible)
        // behind the new slide.
        video.pause();
        video.currentTime = 0;
        // Left the ±1 src window: React only removes the src attribute, which does not
        // reliably abort the in-flight download — load() resets the element so the browser
        // cancels the fetch and releases the decoder.
        if (!video.getAttribute('src') && video.readyState !== 0) video.load();
      }
    });
  };


  const handleStoriesStart = (index:number) => {
    const video = videoRefs.current[index];
    if (!video) return;
    video.play().catch(() => {});
    toggleMuteAll('false');
    setHasinteracted(true);
  };

  const handleVideoClick = useCallback(
    (index: number) => {
      if (!hasInteracted) return;

      const video = videoRefs.current[index];
      if (!video) return;

      if (video.paused) {
        video.play().catch(() => {});
        setIsPlaying(true);
        toggleMuteAll('false');
      } else {
        video.pause();
        setIsPlaying(false);
        toggleMuteAll('true');
      }

      // Show icon for 1 second
      setShowIcon(true);
      if (iconTimeout.current) clearTimeout(iconTimeout.current);
      iconTimeout.current = setTimeout(() => setShowIcon(false), 1000);
    },
    [hasInteracted, toggleMuteAll],
  );

  return (
    <div className="relative h-[calc(100dvh-100px-env(safe-area-inset-bottom))] w-full">

      {isAuth && (
        <Modal
          size="sm"
          isOpen={confirmModal}
          body={(
            <CustomConfirmModalContent
              userSlug={slug ?? ''}
              onSendSuccess={(val: boolean) => {
                if (!val) {
                  setConfirmModal(false);
                  return;
                }
                if (val) {
                  void triggerUnlock({ jobId: 0, jobSlug: '', title: '' });
                }
              }}
            />
          )}
          onClose={() => null}
        />
      )}

      {(!isAllowedPlayingVideo)
        && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black opacity-100">
            <div className="flex items-center space-x-3">
              <div className="size-4 animate-bounce rounded-full bg-secondary" />
              <div className="size-4 animate-bounce rounded-full bg-white [animation-delay:0.15s]" />
              <div className="size-4 animate-bounce rounded-full bg-secondary [animation-delay:0.3s]" />
            </div>
          </div>
        )}

      {items && items.length > 0 && (
        <>
          <Swiper
            direction="vertical"
            modules={[Navigation, Mousewheel, Keyboard, Virtual]}
            // Only the active slide ± neighbors stay mounted (one DOM card per video otherwise).
            virtual
            keyboard={{ enabled: true, onlyInViewport: false }}
            mousewheel
            navigation={{ nextEl: '.next-btn', prevEl: '.prev-btn' }}
            slidesPerView={1}
            observeParents
            observer
            className="h-full"
            onSlideChange={(swiper) => {
              const index = swiper.activeIndex;
              setHasinteracted(true);
              setSwiperIndex(index);

              // setActiveVideoSlug(activeItem.slug ?? '');
              updateVideos(swiper.activeIndex);
              trackEvent({
                eventType: 'view',
                listType: 'stories',
                entityType: 'resume',
                entityId: slug ?? '',
                viewContext: 'detail',
                ...(userId && { userId: userId.toString() }),
                metadata: [
                  { key: 'video', value: slug ?? '' },
                ],
              });
              sendGoogleTagEvent('viewed_interview', 'Custom', { source: 'talent_badge' });
            }}
          >
            {items?.map((item: ApiSchema<'InterviewVideoRead2'>, index :number) => {
              const hlsUrl = findHlsStreamUrl(item.streams);
              const inSrcWindow = Math.abs(index - swiperIndex) <= 1;
              // Native HLS (Safari/iOS): manifest goes straight into src. MSE browsers: the
              // ACTIVE video is fed by hls.js (src stays undefined); neighbors preload MP4
              // metadata. No HLS renditions → plain MP4 as before.
              const playsHlsNatively = Boolean(hlsUrl) && hlsSupport.native && !hlsSupport.mse;
              const playsHlsViaMse = Boolean(hlsUrl) && hlsSupport.mse;
              let videoSrc: string | undefined;
              if (inSrcWindow) {
                if (playsHlsNatively) videoSrc = hlsUrl;
                else if (!(playsHlsViaMse && index === swiperIndex)) videoSrc = item.url;
              }
              return (
                <SwiperSlide
                  key={item.id}
                  virtualIndex={index}
                  className="relative overflow-hidden border-surface-100"
                >

                  {(isLoadingSecurityCheck || !isAllowedPlayingVideo)
                    && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black opacity-100">
                        <div className="flex items-center space-x-3">
                          <div className="size-4 animate-bounce rounded-full bg-secondary" />
                          <div className="size-4 animate-bounce rounded-full bg-white [animation-delay:0.15s]" />
                          <div className="size-4 animate-bounce rounded-full bg-secondary [animation-delay:0.3s]" />
                        </div>
                      </div>
                    )}

                  {/* Overlay Icon */}
                  {' '}
                  {showIcon && (
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/60"
                  >
                    {' '}
                    {isPlaying ? (<PlayIcon className="size-10 animate-fade text-white opacity-90" />) : (
                      <PauseIcon className="size-10 animate-fade text-white opacity-90" />)}
                    {' '}
                  </div>
                  )}
                  {' '}
                  {' '}
                  <div className="absolute inset-x-0 bottom-0 z-1 h-[200px] bg-story-bottom " />

                  {/* Interaction buttons */}
                  <div className="absolute right-4 top-8 z-2 flex flex-col items-center gap-5">
                    <StoriesActionIcons
                      onClick={onClose}
                      icon={<XMarkIcon className="size-7" />}
                    />
                    <StoriesActionIcons
                      onClick={() => toggleMuteAll('prev')}
                      icon={
                    muted ? (
                      <SpeakerXMarkIcon className="size-5" />
                    ) : (
                      <SpeakerWaveIcon className="size-5" />
                    )
                  }
                    />
                    {/* Contact */}
                    {' '}
                    {isAuth ? (
                      <Button type="button" color="secondary" rounding="full" size="sm" className="!size-10 !p-0" onClick={() => { const stored = sessionStorage.getItem('global.cv.detail.unblock'); if (stored === 'true') { void triggerUnlock({ jobId: 0, jobSlug: '', title: '' }); } else { setConfirmModal(true); } }}>
                        {' '}
                        <PhoneIcon className="size-5" />
                        {' '}
                      </Button>
                    ) : (
                      <LinkStyled href="/login" color="secondary" rounding="full" size="sm" className="!size-10 !p-0">
                        {' '}
                        <PhoneIcon className="size-5" />
                        {' '}
                      </LinkStyled>
                    )}

                    {/* Answer this question — logged in, not my own video, not already answered.
                        Resume interview videos carry no question id, so dedupe by id (job cards)
                        OR by text. */}
                    {isAuth && item.question
                      && item.userSlug !== headerInfoData?.userSlug
                      && !(item.questionId != null && answeredQuestionIds.includes(item.questionId))
                      && !answeredQuestionTexts.includes(item.question) && (
                      <InterviewResolver
                        variant="icon"
                        locale={locale}
                        getQuestions={() => Promise.resolve([item.question])}
                        label={item.question}
                      />
                    )}
                  </div>

                  {/* Progress bar */}
                  <div
                    role="presentation"
                    className="absolute left-0 top-0 z-1 h-1 w-full cursor-pointer bg-gray-400"
                    onClick={(e) => {
                      const activeVideo = videoRefs.current[swiperIndex];
                      if (!activeVideo) return;

                      const rect = (e.target as HTMLElement).getBoundingClientRect();
                      const clickX = e.clientX - rect.left;
                      const newTime = (clickX / rect.width) * activeVideo.duration;
                      activeVideo.currentTime = newTime;
                    }}
                  >
                    <div
                      ref={(el) => {
                        if (el) progressRefs.current[index] = el;
                      }}
                      className="h-1 bg-secondary transition-all duration-200 ease-linear"
                    />
                  </div>

                  {/* Video */}
                  <video
                    ref={(el) => {
                      if (el) videoRefs.current[index] = el;
                    }}
                    src={videoSrc}
                    // Only the active video buffers fully; neighbors fetch just metadata so a
                    // single decoder/download runs at a time.
                    preload={index === swiperIndex ? 'auto' : 'metadata'}
                    poster={item.thumbnailUrl ?? ''}
                    className="size-full bg-[#000000] object-cover md:object-contain"
                    loop
                    onClick={() => handleVideoClick(index)}
                    muted={muted}
                    onTimeUpdate={(e) => {
                      const video = e.currentTarget;
                      const progressEl = progressRefs.current[index];
                      if (!progressEl) return;
                      progressEl.style.width = `${
                        (video.currentTime / video.duration) * 100
                      }%`;
                    }}
                    playsInline
                    controls={false}
                  />

                  {/* First click overlay (play) */}
                  {!hasInteracted && (
                  <div
                    role="presentation"
                    onClick={() => handleStoriesStart(index)}
                    className="absolute inset-0 z-1 flex flex-col items-center justify-center bg-dark/30"
                  >
                    <div className="flex size-16 items-center justify-center rounded-full bg-black/60">
                      <PlayIcon className="size-10 text-white opacity-90" />
                    </div>
                  </div>
                  )}

                  {/* Overlay question text */}
                  <div
                    className="absolute inset-x-0 bottom-0 z-1 flex flex-col items-center justify-between px-3 py-5 text-white md:bottom-0 md:flex-row"
                  >
                    <div className="mb-4 text-center text-md font-bold md:text-left">
                      {item.question}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>


          {/* Custom navigation buttons */}
          <div className="absolute right-4 top-1/2 z-2 -mt-12 flex flex-col gap-4">
            <button type="button" className="prev-btn rounded-full bg-surface p-2 text-ink">
              <ArrowUpIcon className="size-5" />
            </button>
            <button type="button" className="next-btn rounded-full bg-surface p-2 text-ink">
              <ArrowDownIcon className="size-5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
